import type {
  CameraWaypoint,
  DeskLoginConfig,
  MfaPcConfig,
  MfaPhoneConfig,
  MfitChatConfig,
  MfitCloudDashConfig,
  ScenarioSpec,
  ScoringEvent,
  TerminalConfig,
} from "./types";
import { officeUnderAttackScenario } from "./office-under-attack.scenario";
import { quishingRestaurantScenario } from "./quishing-restaurant.scenario";

/**
 * Approve or Deny — MFA fatigue and MFA abuse.
 *
 * Office (from office-under-attack): reception terminal, Alex's desk PC, the
 * meeting room. Hacker room (from quishing-restaurant). The donors are only
 * read; every id here is new (prefix mfa_ / wp_mfa_).
 *
 * The lesson is the confusion between Alex's own requests and the attacker's:
 * two genuine requests must be approved, three unexpected ones denied and
 * reported, and the number a fake IT caller hands over must never be typed.
 * Endings: protected (no approval, reported), stopped but unreported, or
 * compromised. Safe call paths still see the attack as "What would have happened".
 */

const OUA = officeUnderAttackScenario;
const OUA_ENV = OUA.scene.environment;
const HACKER_ENV = quishingRestaurantScenario.scene.environments?.find((e) => e.id === "hacker")?.environment;
if (!HACKER_ENV) throw new Error('quishing-restaurant environment "hacker" not found');

function waypoint(scenario: ScenarioSpec, id: string, newId: string, durationS?: number): CameraWaypoint {
  const value = scenario.scene.waypoints.find((w) => w.id === id);
  if (!value) throw new Error(`${scenario.id} waypoint "${id}" not found`);
  return { ...value, id: newId, ...(durationS == null ? {} : { durationS }) };
}

const ASSETS = "/cdn/sim/mfa-fatigue";
const ACCOUNT = "alex.morgan@northbayfinancial.com";
const IT_PHONE = "+41 44 500 22 00";
const REPLAY_NOTE = "If Alex had typed 68, this is what the attacker would have seen.";

const OFFICE_ENV = {
  gltfUrl: OUA_ENV.gltfUrl,
  gltfScale: OUA_ENV.gltfScale,
  gltfPosition: OUA_ENV.gltfPosition,
  gltfRotationDeg: OUA_ENV.gltfRotationDeg,
  gltfColliders: OUA_ENV.gltfColliders,
  procedural: OUA_ENV.procedural,
  enableSplat: false,
  enablePano: false,
  backdropColor: OUA_ENV.backdropColor,
  exposure: OUA_ENV.exposure,
  splat: {},
  deskPc: OUA_ENV.deskPc,
  receptionTerminal: OUA_ENV.receptionTerminal,
  deskMobile: {
    initial: "desk",
    spots: {
      // left of the keyboard on Alex's desk (deskPc [0.2,0,1.7] rotated 180)
      desk: { position: [0.56, 0.765, 1.5] as [number, number, number], rotationDeg: 8 },
      // on the meeting-room table
      meeting: { position: [-6.9, 0.765, 3.55] as [number, number, number], rotationDeg: -78 },
    },
  },
};

// ---------------------------------------------------------------- scoring
const EVENTS: ScoringEvent[] = [
  { id: "mfa_watched_film", label: "Watched the MFA fatigue film", kind: "positive", weight: 5, dimension: "information" },
  { id: "mfa_approved_own", label: "Approved your own sign-in with the matching number", kind: "positive", weight: 5, dimension: "identity" },
  { id: "mfa_approved_stepup", label: "Approved the step-up check you started", kind: "positive", weight: 5, dimension: "identity" },
  { id: "mfa_denied_push", label: "Denied sign-in requests you did not start", kind: "positive", weight: 10, dimension: "identity", collectEvidence: true },
  { id: "mfa_reported_push", label: "Reported the unexpected sign-in requests", kind: "positive", weight: 25, dimension: "operations", collectEvidence: true },
  {
    id: "mfa_tried_number",
    label: "Typed a number into a sign-in request you did not start",
    kind: "risk",
    weight: 25,
    dimension: "identity",
    collectEvidence: true,
    detail: "A request you did not start comes from someone else's sign-in. Typing a number is trying to let them in.",
  },
  {
    id: "mfa_unreported_push",
    label: "Did not report three sign-in requests you did not start",
    kind: "risk",
    weight: 15,
    dimension: "operations",
    detail: "Requests you did not start mean someone has your password. Only IT Security can change it and block them.",
  },
  { id: "mfa_call_verified", label: "Hung up and called IT on the official number", kind: "positive", weight: 25, dimension: "operations", collectEvidence: true },
  { id: "mfa_call_questioned", label: "Asked the caller to prove who he was", kind: "positive", weight: 5, dimension: "identity" },
  {
    id: "mfa_call_complied",
    label: "Followed the instructions of an unverified 'IT' caller",
    kind: "risk",
    weight: 20,
    dimension: "identity",
    collectEvidence: true,
    detail: "Caller ID and a confident voice are not proof. Real IT does not need you to type a number they give you.",
  },
  { id: "mfa_trap_inspected", label: "Checked the address of the verification page", kind: "positive", weight: 5, dimension: "information" },
  { id: "mfa_trap_rejected", label: "Rejected the request with the caller's number", kind: "positive", weight: 20, dimension: "identity", collectEvidence: true },
  { id: "mfa_trap_reported", label: "Reported the fake IT call and page", kind: "positive", weight: 20, dimension: "operations", collectEvidence: true },
  {
    id: "mfa_approved_attacker",
    label: "Typed the caller's number 68 and approved the request",
    kind: "risk",
    weight: 70,
    dimension: "identity",
    collectEvidence: true,
    detail: "The number came from the attacker's own sign-in screen. Approving it signed the attacker in as Alex.",
  },
];
const REPORTED = ["mfa_reported_push", "mfa_call_verified", "mfa_trap_reported"];

// ---------------------------------------------------------------- devices
const terminal: TerminalConfig = {
  title: "NORTHBAY FINANCIAL — SECURITY BRIEFING",
  offLabel: "Switch on the terminal",
  voiceId: "nova",
  lines: [
    "Welcome to NorthBay Financial. Today is about the sign-in requests on your phone: Microsoft Authenticator.",
    "Multi-factor authentication protects your account even when someone knows your password. But attackers have learned to use it against you.",
    "They send request after request, hoping you approve one. Or they call you as IT and give you the number to type.",
    "You are Alex Morgan from Finance. Your quarterly report is due before the ten o'clock management meeting. Head to your desk.",
  ],
  continueLabel: "Go to my desk",
};

const deskLogin: DeskLoginConfig = {
  screenTitle: "NorthBay Financial — locked",
  userName: "Alex Morgan",
  hint: "Your sign-in details are in the note on the right. Click it to fill them in.",
  credential: { email: ACCOUNT, password: "Harbour-Light-42", source: "Your sign-in" },
  continueLabel: "Sign In",
};

const intranet: MfitChatConfig = {
  frame: "desktop",
  mode: "intranet",
  clock: "09:03",
  date: "Tue 13 Oct",
  intranet: {
    url: "intranet.northbay.internal",
    siteName: "NorthBay Intranet",
    navItems: ["News", "People", "IT help", "Documents"],
    headline: "Good morning, Alex",
    news: [
      { title: "Q3 reporting", body: "Quarterly reports are due in the Finance Portal before today's management meeting." },
      { title: "Meeting room 2", body: "Booked for the management meeting from 10:00." },
      { title: "IT service desk", body: `Call ${IT_PHONE} or open a ticket in the IT portal.` },
    ],
    notice: {
      kicker: "IT Security · Before you sign in today",
      title: "What MFA fatigue is, and how to beat it",
      body: "A four-minute film from IT Security about sign-in requests on your phone. You can skip it and carry on.",
      watchLabel: "Watch the video",
      dismissLabel: "Skip",
    },
    watchEventId: "mfa_watched_film",
    video: {
      title: "MFA fatigue: approve or deny?",
      src: "/cdn/sim/awareness-videos/mfa-fatigue/mfa-fatigue.mp4",
      poster: `${ASSETS}/img/film-poster.jpg`,
      tip: "Only approve a sign-in request you started yourself, and only with the number on your own screen.",
      closeLabel: "Close the video",
    },
  },
  messages: [],
  guide: {
    desktop: { title: "Check the intranet", body: "Click Browser on the desktop.", actionLabel: "Browser" },
    intranet: { title: "Before you start", body: "IT Security has posted a short film. Watch it, or skip it and carry on." },
    video: { title: "MFA fatigue", body: "Watch the film. You can click Close the video at any time.", actionLabel: "Close the video" },
  },
};

const PORTAL = { name: "Finance Portal", url: "finance.northbay.internal/reports" };

const pcSignin: MfaPcConfig = {
  mode: "signin",
  clock: "09:05",
  date: "Tue 13 Oct",
  portal: PORTAL,
  number: "42",
  task: {
    kicker: "Task · 09:05",
    title: "Quarterly report due at 10:00",
    body: "Your quarterly financial report is due before the 10:00 management meeting. Open the Finance Portal.",
  },
  guide: {
    desktop: { title: "Your report is due at 10:00", body: "Open the Finance Portal. Click Browser on the desktop.", actionLabel: "Browser" },
    intranet: { title: "Open the Finance Portal", body: "Click Finance Portal on the intranet.", actionLabel: "Finance Portal" },
    msnumber: {
      title: "Microsoft wants a second check",
      body: "You just signed in yourself. Note the number on the screen, then click Pick up your phone.",
      actionLabel: "Pick up your phone",
    },
  },
};

const phoneOwn: MfaPhoneConfig = {
  mode: "auth",
  clock: "09:05",
  ownerName: "Alex",
  requests: [{ id: "own", app: "Finance Portal", account: ACCOUNT, location: "Zurich, Switzerland", number: "42" }],
  stuckTip: "The number is on your PC screen, under Approve sign in request.",
  denyTip: {
    title: "This one was you",
    body: "You opened the Finance Portal a moment ago, so this request is yours. Denying it only blocks your own work.",
    retryLabel: "Send the request again",
  },
  events: { approved_legit: "mfa_approved_own" },
  guide: {
    lock: { title: "Your phone", body: "A sign-in request has arrived. Your PC shows the number 42. Click the Authenticator notification." },
    request: {
      title: "Is this you? Your PC shows 42",
      body: "You started this sign-in, the app is the Finance Portal, and the number on your PC is 42. Type 42, then click Yes.",
      actionLabel: "Yes",
    },
    denytip: { title: "That was your own sign-in", body: "Your PC shows 42. Click Send the request again.", actionLabel: "Send the request again" },
    approved: { title: "Approved", body: "Back to the PC." },
  },
};

const pcPortal: MfaPcConfig = {
  mode: "portal",
  clock: "09:06",
  date: "Tue 13 Oct",
  portal: PORTAL,
  info: {
    kicker: "Signed in",
    title: "That request was genuine",
    body:
      "You started the sign-in. The request named the app you were opening. The number on your phone matched the number on your own screen.\n\nAll three together are what make a request safe to approve.",
    continueLabel: "Work on the report",
  },
  guide: { info: { title: "Signed in", body: "Read why this request was safe, then click Work on the report.", actionLabel: "Work on the report" } },
};

const pcSheet: MfaPcConfig = {
  mode: "sheet",
  clock: "09:12",
  date: "Tue 13 Oct",
  portal: PORTAL,
  sheet: {
    intranetUrl: "intranet.northbay.internal/documents",
    siteName: "NorthBay Intranet",
    fileName: "Q3_Financial_Report",
    fileUrl: "docs.google.com/spreadsheets/d/q3-financial-report",
    columns: ["Line item", "Q1", "Q2", "Q3"],
    rows: [
      ["Revenue (CHF k)", "4,210", "4,385", ""],
      ["Operating costs", "2,960", "3,010", "3,072"],
      ["Marketing", "410", "395", "402"],
      ["IT and licences", "288", "291", "296"],
      ["Operating result", "552", "689", ""],
    ],
    fillRow: 0,
    fillCol: 3,
    fillValue: "4,512",
    fillLabel: "Fill in Q3 revenue",
  },
  phoneLabel: "Pick up your phone",
  guide: {
    desktop: { title: "Back to the report", body: "Open the shared spreadsheet. Click Browser.", actionLabel: "Browser" },
    intranet: { title: "Shared documents", body: "Click Documents on the intranet.", actionLabel: "Documents" },
    docs: { title: "The Q3 spreadsheet", body: "Click Q3_Financial_Report.", actionLabel: "Q3_Financial_Report" },
    sheet: { title: "The revenue figure is missing", body: "Click Fill in Q3 revenue.", actionLabel: "Fill in Q3 revenue" },
    typing: { title: "Working", body: "Alex types in the figure." },
    buzz: {
      title: "Your phone is vibrating",
      body: "You are working in the spreadsheet. You did not open anything new. Click Pick up your phone.",
      actionLabel: "Pick up your phone",
    },
  },
};

const phoneBomb: MfaPhoneConfig = {
  mode: "bomb",
  clock: "09:12",
  ownerName: "Alex",
  requests: [
    { id: "b1", app: "Microsoft 365", account: ACCOUNT, location: "Amsterdam, Netherlands", timeoutS: 20 },
    { id: "b2", app: "Microsoft 365", account: ACCOUNT, location: "Amsterdam, Netherlands", timeoutS: 20 },
    { id: "b3", app: "Microsoft 365", account: ACCOUNT, location: "Amsterdam, Netherlands", timeoutS: 20 },
  ],
  reportPrompt: {
    title: "Report suspicious activity?",
    body: "IT Security will be told that someone tried to sign in to your account and you denied it.",
    reportLabel: "Report",
    skipLabel: "Not now",
  },
  events: {
    denied_bombing: "mfa_denied_push",
    reported_bombing: "mfa_reported_push",
    tried_number: "mfa_tried_number",
    unreported: "mfa_unreported_push",
  },
  guide: {
    lock: { title: "A sign-in request", body: "You did not start a sign-in. Open the notification, or dismiss it." },
    request: { title: "Are you trying to sign in?", body: "You are not signing in anywhere. There is no number on your screen. Decide what to do." },
    report: { title: "Report it?", body: "Choose whether to tell IT Security." },
    between: { title: "Another one?", body: "Wait a moment." },
    summary: { title: "Three requests in two minutes", body: "Click Continue.", actionLabel: "Continue" },
  },
};

const pcUpload: MfaPcConfig = {
  mode: "upload",
  clock: "09:15",
  date: "Tue 13 Oct",
  portal: PORTAL,
  number: "27",
  calendar: { title: "Management meeting", body: "Begins in 5 minutes · Meeting room 2" },
  phoneLabel: "Pick up your phone",
  guide: {
    upload_wait: { title: "The report is ready", body: "Upload it to the Finance Portal." },
    upload: { title: "Five minutes left", body: "Click Upload report.", actionLabel: "Upload report" },
    stepup: {
      title: "Another check",
      body: "Uploading a confidential report asks for one more check. You started it. Note the number, then click Pick up your phone.",
      actionLabel: "Pick up your phone",
    },
  },
};

const phoneStepup: MfaPhoneConfig = {
  mode: "auth",
  clock: "09:15",
  ownerName: "Alex",
  requests: [{ id: "step", app: "Finance Portal", account: ACCOUNT, location: "Zurich, Switzerland", number: "27" }],
  stuckTip: "You clicked Upload report a moment ago. The number on your PC is 27.",
  denyTip: {
    title: "The right answer is not always No",
    body: "You clicked Upload report a moment ago, and your screen shows the number. This request is yours. If you deny every request, you cannot finish your own work.",
    retryLabel: "Send the request again",
  },
  events: { approved_legit: "mfa_approved_stepup" },
  guide: {
    lock: { title: "Another request", body: "Your PC shows the number 27. Open the Authenticator notification." },
    request: {
      title: "Is this you? Your PC shows 27",
      body: "You just clicked Upload report, and the number on your PC is 27. Is this request yours? Decide, then type 27 if it is.",
    },
    denytip: { title: "This one was yours", body: "Your PC shows 27. Click Send the request again.", actionLabel: "Send the request again" },
    approved: { title: "Approved", body: "Back to the PC." },
  },
};

const pcUploaded: MfaPcConfig = {
  mode: "uploaded",
  clock: "09:16",
  date: "Tue 13 Oct",
  portal: PORTAL,
  info: {
    kicker: "Uploaded",
    title: "Same app, same sound, different story",
    body:
      "Every request on your phone today looked and sounded the same. What told them apart was you: did you start it, and does the number come from your own screen?\n\nThe report is in. Time for the meeting.",
    continueLabel: "Go to the meeting",
  },
  guide: { info: { title: "Report uploaded", body: "Click Go to the meeting.", actionLabel: "Go to the meeting" } },
};

const TRAP: NonNullable<MfaPhoneConfig["trap"]> = {
  smsFrom: "IT Service Desk",
  smsText: "NorthBay IT: complete your verification here: northbay-idverify.com/reset",
  url: "https://northbay-idverify.com/reset",
  pageKicker: "Security maintenance",
  pageTitle: "Corporate Identity Verification",
  pageBody: "To restore secure access, please complete verification. Enter this number in Microsoft Authenticator:",
  number: "68",
  location: "Zurich, Switzerland",
  inspect: {
    title: "Website details",
    rows: [
      { label: "Address", value: "northbay-idverify.com", warn: true },
      { label: "Registered", value: "3 days ago", warn: true },
      { label: "NorthBay's sign-in", value: "login.microsoftonline.com" },
    ],
    note: "This is not a NorthBay or Microsoft address. A page cannot 'clear' anything by asking you to type a number.",
  },
  closeLabel: "Close the page and hang up",
  report: {
    title: "Tell IT Security?",
    body: `Call the IT service desk on ${IT_PHONE}, the number on the intranet, and tell them about the call and the page.`,
    reportLabel: "Call the IT service desk",
    skipLabel: "Not now",
  },
};

const phoneCall: MfaPhoneConfig = {
  mode: "call",
  clock: "09:17",
  ownerName: "Alex",
  requests: [{ id: "trap", app: "Microsoft 365", account: ACCOUNT }],
  call: {
    callerName: "IT Service Desk",
    callerSubtitle: "+41 44 500 21 90",
    voiceId: "ash",
    greeting: [
      "Hi Alex, this is Daniel from IT Security.",
      "We've detected repeated failed sign-in attempts on your Microsoft account from Amsterdam. I'm trying to fix it before your account gets locked.",
    ],
    rounds: [
      {
        prompt: "What do you say?",
        options: [
          { id: "follow", label: "OK, what do I need to do?", comply: true },
          { id: "question", label: "How do I know you're really from IT?", eventId: "mfa_call_questioned", reply: [
            "Fair question. I can see your account, alex.morgan at northbayfinancial.com, Finance department. And I'm calling from the service desk number.",
            "But we need to move fast, otherwise the account locks and you miss your meeting.",
          ] },
          {
            id: "hangup",
            label: "I'll hang up and call the IT service desk myself.",
            hangup: true,
            eventId: "mfa_call_verified",
            reply: ["Uh, sure. But be quick about it."],
            result: {
              title: "The real IT desk knows nothing about it",
              body: `You called ${IT_PHONE}, the number on the intranet. IT Security confirms nobody called you. They reset your password, block the sign-ins from Amsterdam and warn your colleagues.`,
            },
          },
        ],
      },
      {
        prompt: "What do you say now?",
        options: [
          { id: "follow2", label: "OK, tell me what to do.", comply: true },
          {
            id: "hangup2",
            label: "I'll call the service desk back on the number I know.",
            hangup: true,
            eventId: "mfa_call_verified",
            reply: ["Fine. Just be quick."],
            result: {
              title: "The real IT desk knows nothing about it",
              body: `You called ${IT_PHONE}, the number on the intranet. IT Security confirms nobody called you. They reset your password, block the sign-ins from Amsterdam and warn your colleagues.`,
            },
          },
        ],
      },
    ],
    complyLines: [
      "Great. You'll receive another Microsoft Authenticator request. I'll give you the number to enter. That will clear the pending authentication.",
      "I'm sending you a link by text message now. Open it, and you'll see the number.",
    ],
  },
  trap: TRAP,
  events: {
    call_complied: "mfa_call_complied",
    trap_inspected: "mfa_trap_inspected",
    trap_rejected: "mfa_trap_rejected",
    trap_reported: "mfa_trap_reported",
    approved_attacker: "mfa_approved_attacker",
  },
  guide: {
    ring: { title: "Your phone is ringing", body: "The phone on the table shows IT Service Desk. Click Answer.", actionLabel: "Answer" },
    call: { title: "On the call", body: "Daniel from IT Security is speaking. Listen." },
    deciding: { title: "Your reply", body: "Choose what you say to Daniel. Your options are on the phone." },
    result: { title: "You checked", body: "Click Continue.", actionLabel: "Continue" },
    sms: { title: "A text message", body: "Daniel has sent you a link. Open the message." },
    page: { title: "A verification page", body: "The page shows a number. You can check the address at the top. Decide what to do." },
    inspect: { title: "Who runs this page?", body: "Read the details, then go back to the page." },
    trapauth: {
      title: "A real Microsoft request",
      body: "This request really comes from Microsoft. Someone is signing in to your account right now. Decide what to do.",
    },
    trapreport: { title: "Tell IT Security?", body: "Choose whether to call the IT service desk." },
    frozen: { title: "Approved", body: "" },
    page_replay: { title: "What would have happened", body: "Wait for the Authenticator notification, then open it." },
    trapauth_replay: { title: "What would have happened", body: "Type 68, the number from the page, then click Yes.", actionLabel: "Yes" },
  },
};

const phoneReplay: MfaPhoneConfig = { ...phoneCall, replay: true };
const phonePractice: MfaPhoneConfig = {
  ...phoneCall,
  practice: true,
  guide: {
    ...phoneCall.guide,
    page: { title: "Try it again", body: "The same page, the same number. This time, decide differently." },
  },
};

// ---------------------------------------------------------------- attacker console
const peekDash: MfitCloudDashConfig = {
  view: "timeline",
  kicker: "09:14 · Attacker view",
  title: "Session relay · alex.morgan",
  operator: "op: nb-finance",
  voiceId: "onyx",
  narration: [
    { text: "This is the other side. The attacker already has Alex's password, bought from an old data breach.", focus: "p1" },
    {
      text: "Each request on Alex's phone was the attacker signing in. Microsoft asked for a number, and only the person looking at the sign-in screen can see it.",
      focus: "p2",
    },
    { text: "Sending request after request is called MFA fatigue. The attacker hopes Alex gets tired or confused, and approves one.", focus: "p2" },
    { text: "Next, the attacker will call Alex as IT, and hand over the number himself.", focus: "p3" },
  ],
  steps: [
    { id: "p1", title: "Password obtained", body: "alex.morgan@northbayfinancial.com, from an old breach list." },
    {
      id: "p2",
      title: "Three sign-ins from Amsterdam",
      body: "",
      stateIf: [
        { events: ["mfa_tried_number"], state: "failed", statusLabel: "Wrong number", body: "Alex typed a number. It could not match: Alex never saw the sign-in screen." },
        { events: ["mfa_reported_push"], state: "denied", statusLabel: "Reported", body: "Alex chose No, it's not me, and reported it. IT Security has been alerted." },
        { events: ["mfa_denied_push"], state: "denied", statusLabel: "Denied", body: "Alex chose No, it's not me. Nobody has told IT Security yet." },
        { state: "failed", statusLabel: "Timed out", body: "Alex did not react. The requests timed out, and nobody told IT Security." },
      ],
    },
    { id: "p3", title: "Next: call Alex as IT Security", body: "Read out a number, and ask Alex to type it.", state: "pending", statusLabel: "Planned" },
  ],
  continueLabel: "Continue",
};

const attackDash: MfitCloudDashConfig = {
  view: "timeline",
  kicker: "09:20 · Attacker view",
  title: "Session relay · alex.morgan",
  operator: "op: nb-finance",
  voiceId: "onyx",
  narration: [
    { text: "The attacker already knew Alex's password.", focus: "a1" },
    {
      text: "The authentication requests weren't fake. They came from the real Microsoft sign-in service, because someone else was trying to sign in.",
      focus: "a2",
    },
    { text: "Then the attacker called as IT, to keep Alex busy and to sound helpful.", focus: "a3" },
    {
      text: "When Microsoft showed the number 68 on the attacker's screen, he copied it onto a page that looked like IT's own, and asked Alex to type it.",
      focus: "a4",
    },
  ],
  steps: [
    { id: "a1", title: "Password obtained", body: "From an old breach list." },
    {
      id: "a2",
      title: "Three push requests",
      body: "",
      stateIf: [
        { events: ["mfa_tried_number"], state: "failed", statusLabel: "Wrong number", body: "Alex typed a guessed number. Microsoft denied it." },
        { events: ["mfa_reported_push"], state: "denied", statusLabel: "Reported", body: "Denied and reported by Alex." },
        { events: ["mfa_denied_push"], state: "denied", statusLabel: "Denied", body: "Denied by Alex, not reported." },
        { state: "failed", statusLabel: "Timed out", body: "Timed out. Nobody reported them." },
      ],
    },
    {
      id: "a3",
      title: "Phone call as 'Daniel from IT Security'",
      body: "",
      stateIf: [
        { replay: true, state: "done", statusLabel: "Worked", body: "What would have happened: Alex follows Daniel's instructions." },
        { events: ["mfa_call_verified"], state: "denied", statusLabel: "Stopped", body: "Alex hung up and called the real IT service desk." },
        { state: "done", statusLabel: "Worked", body: "Alex stayed on the line and opened the link." },
      ],
    },
    {
      id: "a4",
      title: "Relayed the number 68 to the fake IT page",
      body: "",
      stateIf: [
        { replay: true, state: "success", statusLabel: "Signed in", body: "What would have happened: Alex types 68." },
        { events: ["mfa_approved_attacker"], state: "success", statusLabel: "Signed in", body: "Alex typed 68. The attacker is signed in." },
        { events: ["mfa_trap_rejected"], state: "denied", statusLabel: "Denied", body: "Alex chose No, it's not me." },
        { state: "failed", statusLabel: "Not reached", body: "The call ended before Alex saw the page." },
      ],
    },
  ],
  continueLabel: "Continue",
};

const SESSION_VIEW: Partial<MfitCloudDashConfig> = {
  view: "access",
  title: "Microsoft 365 · signed in as Alex Morgan",
  narration: [
    { text: "One approved request is enough. The attacker is signed in as Alex, with the same mailbox, files and chats.", focus: "mail" },
    { text: "What they can reach depends on the company's controls. This session will not ask Alex's phone again.", focus: "f1" },
  ],
  access: [
    { id: "mail", label: "Mailbox", value: "Outlook · 4,812 emails", detail: "Can read and send as Alex" },
    { id: "files", label: "Files", value: "OneDrive and SharePoint", detail: "Everything Alex can open" },
    { id: "chats", label: "Teams", value: "Chats with Finance and Management", detail: "Can message as Alex" },
    { id: "session", label: "Session", value: "Amsterdam · approved at 09:19", detail: "Approved with the number 68" },
  ],
  findings: [
    { id: "f1", kind: "sheet", title: "Q3_Financial_Report.xlsx", meta: "Finance Portal · uploaded 09:16", lines: ["Revenue Q3: CHF 4,512k", "Shared with: Management meeting"] },
    { id: "f2", kind: "mail", title: "Re: Supplier payment run on Thursday", meta: "Inbox · Finance", lines: ["Updated bank details are in the attached sheet."] },
    { id: "f3", kind: "doc", title: "Board pack Q3 (draft)", meta: "Teams · Management channel" },
  ],
};

const resultDash: MfitCloudDashConfig = {
  view: "denied",
  kicker: "09:20 · Attacker view",
  title: "Microsoft 365 · alex.morgan",
  operator: "op: nb-finance",
  voiceId: "onyx",
  narration: [
    { text: "Without the number, the sign-in failed. A password on its own was not enough.", focus: "denied" },
    { text: "Once IT Security knows, they change the password and block the attacker's sign-ins.", focus: "denied" },
  ],
  steps: [
    { id: "r1", title: "Password", body: "Correct.", state: "done" },
    { id: "r2", title: "Multi-factor authentication", body: "Not completed by Alex.", state: "denied" },
  ],
  deniedNote: "Sign-in failed: multi-factor authentication was not completed.",
  variants: [
    { ifReplay: true, config: { ...SESSION_VIEW, kicker: "09:20 · What would have happened" } },
    { ifEvents: ["mfa_approved_attacker"], config: SESSION_VIEW },
  ],
  routes: [
    { key: "compromised", ifEvents: ["mfa_approved_attacker"] },
    { key: "protected", ifEvents: REPORTED },
    { key: "unresolved" },
  ],
  continueLabel: "Continue",
};

// ---------------------------------------------------------------- scenario
export const mfaFatigueScenario: ScenarioSpec = {
  version: 1,
  id: "mfa-fatigue",
  title: "Approve or Deny",
  topic: "MFA fatigue and MFA abuse",
  language: "en",
  summary: {
    goal: "Tell your own sign-in requests apart from an attacker's, and never type a number someone else gives you.",
    youDo: "Approve two genuine Microsoft Authenticator requests, handle a burst of requests you did not start, and take a call from 'IT Security', then watch the attack from the attacker's side.",
    duration: "12-15 min, plus an optional 4-minute film",
    tags: ["mfa", "leaked_credentials", "voice_phishing", "verification", "incident_reporting"],
    audience: ["all_staff", "finance"],
    needs: ["sound"],
  },
  ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
  ambientVolume: 0.14,
  music: "none",
  passThreshold: 60,
  riskMeter: false,
  exposureMap: true,
  allowFreeExplore: false,

  scene: {
    environment: OFFICE_ENV,
    environments: [
      { id: "office", environment: OFFICE_ENV, ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3", ambientVolume: 0.14, music: "none" },
      { id: "hacker", environment: HACKER_ENV, ambientUrl: "", music: "dark" },
    ],
    props: [],
    waypoints: [
      waypoint(OUA, "wp_reception_wide", "wp_mfa_reception"),
      waypoint(OUA, "wp_terminal_close", "wp_mfa_terminal"),
      waypoint(OUA, "wp_desk_signin", "wp_mfa_desk"),
      waypoint(OUA, "wp_desk_signin", "wp_mfa_desk_back", 1.5),
      { id: "wp_mfa_desk_wide", position: [0.48, 1.42, 0.62], target: [0.4, 0.86, 1.58], fov: 50, durationS: 1.5, ease: "easeInOut" },
      { id: "wp_mfa_phone", position: [0.56, 1.1, 1.3], target: [0.56, 0.765, 1.51], fov: 40, durationS: 1.3, ease: "easeInOut" },
      waypoint(OUA, "wp_meeting_establish", "wp_mfa_meeting"),
      { id: "wp_mfa_meeting_phone", position: [-6.55, 1.12, 3.55], target: [-6.9, 0.765, 3.55], fov: 42, durationS: 1.8, ease: "easeInOut" },
      { id: "wp_mfa_meeting_phone_cut", position: [-6.55, 1.12, 3.55], target: [-6.9, 0.765, 3.55], fov: 42, durationS: 0, ease: "easeInOut" },
      waypoint(quishingRestaurantScenario, "wp_hacker_establish", "wp_mfa_hacker"),
      waypoint(quishingRestaurantScenario, "wp_hacker_laptop", "wp_mfa_hacker_laptop"),
    ],
    initialWaypointId: "wp_mfa_reception",
  },

  devices: [
    { id: "mfa_terminal_kiosk", kind: "terminal", config: terminal },
    { id: "mfa_desklogin", kind: "desklogin", config: deskLogin },
    { id: "mfa_pc_intranet", kind: "mfitchat", config: intranet },
    { id: "mfa_pc_signin", kind: "mfapc", config: pcSignin },
    { id: "mfa_phone_own", kind: "mfaphone", config: phoneOwn },
    { id: "mfa_pc_portal", kind: "mfapc", config: pcPortal },
    { id: "mfa_pc_sheet", kind: "mfapc", config: pcSheet },
    { id: "mfa_phone_bomb", kind: "mfaphone", config: phoneBomb },
    { id: "mfa_dash_peek", kind: "mfitclouddash", config: { ...peekDash, routes: [
      { key: "attempted", ifEvents: ["mfa_tried_number"] },
      { key: "reported", ifEvents: ["mfa_reported_push"] },
      { key: "denied", ifEvents: ["mfa_denied_push"] },
      { key: "ignored" },
    ] } },
    { id: "mfa_pc_upload", kind: "mfapc", config: pcUpload },
    { id: "mfa_phone_stepup", kind: "mfaphone", config: phoneStepup },
    { id: "mfa_pc_uploaded", kind: "mfapc", config: pcUploaded },
    { id: "mfa_phone_call", kind: "mfaphone", config: phoneCall },
    { id: "mfa_phone_replay", kind: "mfaphone", config: phoneReplay },
    { id: "mfa_phone_practice", kind: "mfaphone", config: phonePractice },
    { id: "mfa_dash_attack", kind: "mfitclouddash", config: attackDash },
    { id: "mfa_dash_result", kind: "mfitclouddash", config: resultDash },
  ],

  voiceAttackers: {
    mfa_fatigue: {
      persona: { name: "Daniel", role: "Fake IT Security caller", pretext: "it_support", voiceId: "ash" },
      goal: "Get Alex to approve the attacker's Microsoft sign-in",
      opening: "",
      allowedTopics: [],
      scoringEvents: EVENTS,
    },
  },

  initialBeatId: "mfa_intro",
  beats: [
    {
      id: "mfa_intro",
      type: "intro",
      title: "Approve\nor Deny",
      kicker: "MFA fatigue and MFA abuse",
      highlight: "Deny",
      icon: "shield",
      ctaLabel: "Start",
      guide: {
        title: "Tuesday, 09:00 at NorthBay Financial",
        body: "You are Alex Morgan from Finance. The quarterly report is due before the 10:00 management meeting, and your phone keeps asking you to approve sign-ins.",
      },
    },
    { id: "mfa_cam_terminal", type: "camera", waypointId: "wp_mfa_terminal", walkSfx: true },
    {
      id: "mfa_terminal",
      type: "device",
      deviceId: "mfa_terminal_kiosk",
      guide: { title: "Security briefing", body: "Switch on the terminal at the front desk. You can skip the briefing at any time.", actionLabel: "Switch on the terminal" },
      completeOn: { action: "continue" },
    },

    // ---- Scene 1 · 09:05 · a genuine sign-in
    { id: "mfa_cam_desk", type: "camera", waypointId: "wp_mfa_desk", walkSfx: true },
    {
      id: "mfa_login",
      type: "device",
      deviceId: "mfa_desklogin",
      guide: { title: "Sign in", body: "Click the login screen, fill in your details from the note, then click Sign In.", actionLabel: "Sign In" },
      completeOn: { action: "continue" },
    },
    { id: "mfa_intranet", type: "device", deviceId: "mfa_pc_intranet", guide: { title: "Check the intranet", body: "Click Browser on the desktop." }, completeOn: { action: "continue" } },
    { id: "mfa_signin", type: "device", deviceId: "mfa_pc_signin", guide: { title: "Your report", body: "Open the Finance Portal." }, completeOn: { action: "continue" } },
    { id: "mfa_cam_wide1", type: "camera", waypointId: "wp_mfa_desk_wide", mobileBuzz: true, guide: { title: "Your phone", body: "It buzzes on the desk." } },
    { id: "mfa_cam_phone1", type: "camera", waypointId: "wp_mfa_phone", mobileBuzz: true, guide: { title: "Your phone", body: "A sign-in request." } },
    { id: "mfa_auth_own", type: "device", deviceId: "mfa_phone_own", guide: { title: "Your phone", body: "Open the request." }, completeOn: { action: "continue" } },
    { id: "mfa_cam_back1", type: "camera", waypointId: "wp_mfa_desk_back" },
    { id: "mfa_portal", type: "device", deviceId: "mfa_pc_portal", guide: { title: "Signed in", body: "Click Work on the report." }, completeOn: { action: "continue" } },

    // ---- Scene 2 · 09:12 · requests you did not start
    { id: "mfa_title_0912", type: "title", title: "09:12", autoAdvanceMs: 2600, guide: { title: "Seven minutes later", body: "Alex is working on the report." } },
    { id: "mfa_sheet", type: "device", deviceId: "mfa_pc_sheet", guide: { title: "The report", body: "Open the shared spreadsheet." }, completeOn: { action: "continue" } },
    { id: "mfa_cam_wide2", type: "camera", waypointId: "wp_mfa_desk_wide", mobileBuzz: true, guide: { title: "Your phone", body: "It buzzes again." } },
    { id: "mfa_cam_phone2", type: "camera", waypointId: "wp_mfa_phone", mobileBuzz: true, guide: { title: "Your phone", body: "A sign-in request." } },
    { id: "mfa_bomb", type: "device", deviceId: "mfa_phone_bomb", guide: { title: "Your phone", body: "A sign-in request you did not start." }, completeOn: { action: "continue" } },
    {
      id: "mfa_peek_cut",
      type: "camera",
      waypointId: "wp_mfa_hacker",
      environmentId: "hacker",
      fadeCut: true,
      guide: { title: "Meanwhile", body: "Somewhere else." },
    },
    {
      id: "mfa_peek_title",
      type: "title",
      title: "The attacker's side",
      kicker: "The other side",
      autoAdvanceMs: 11000,
      guide: { title: "The attacker's side", body: "This is a fictional criminal who already has Alex's password from an old data breach. They want Alex to approve just one sign-in, so they can get into Alex's Finance account. Here is what they could see after your choice." },
    },
    { id: "mfa_peek_push", type: "camera", waypointId: "wp_mfa_hacker_laptop", guide: { title: "The attacker's laptop", body: "The attack log." } },
    { id: "mfa_peek_dash", type: "device", deviceId: "mfa_dash_peek", guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    {
      id: "mfa_teach_bomb_tried",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "You were not signing in anywhere, so there was no number on any screen of yours. Typing a guess into a request you did not start is trying to let someone in. This time the number could not match.",
        spot: ["A request while you were not signing in", "A location you are not in", "Several requests in a row"],
        protect: ["Choose No, it's not me", "Report it in Authenticator, or call IT", "Tell IT: someone has your password"],
        remember: "No sign-in of your own means no number to type. Deny and report.",
        continueLabel: "Back to work",
      },
    },
    {
      id: "mfa_teach_bomb_reported",
      type: "teach",
      teach: {
        outcome: "safe",
        body: "You were not signing in, so the requests could not be yours. You denied them and told IT Security, who can now change your password and block the sign-ins.",
        spot: ["A request while you were not signing in", "A location you are not in", "Several requests in a row"],
        protect: ["Choose No, it's not me", "Report it", "Expect IT to reset your password"],
        remember: "Requests you did not start mean someone has your password.",
        continueLabel: "Back to work",
      },
    },
    {
      id: "mfa_teach_bomb_denied",
      type: "teach",
      teach: {
        outcome: "safe",
        heading: {
          eyebrow: "SAFE, BUT NOT DONE",
          title: "Denying kept the attacker out. Reporting stops them.",
          spotEyebrow: "WHAT GAVE IT AWAY",
          spotTitle: "The signs on your phone",
          protectEyebrow: "NEXT TIME",
          protectTitle: "Deny, then report",
        },
        body: "You denied the requests, so the attacker stayed out. But they still have your password and will try again. Only IT Security can change it and block them, and they only know if you report it.",
        spot: ["A request while you were not signing in", "A location you are not in", "Several requests in a row"],
        protect: ["Choose No, it's not me", "Then choose Report, or call IT", "Several requests mean someone has your password"],
        remember: "Deny keeps them out today. Report keeps them out tomorrow.",
        continueLabel: "Back to work",
      },
    },
    {
      id: "mfa_teach_bomb_ignored",
      type: "teach",
      teach: {
        outcome: "safe",
        heading: {
          eyebrow: "IGNORED, NOT REPORTED",
          title: "The requests timed out. The attacker did not.",
          spotEyebrow: "WHAT GAVE IT AWAY",
          spotTitle: "The signs on your phone",
          protectEyebrow: "NEXT TIME",
          protectTitle: "Deny, then report",
        },
        body: "Ignoring the requests did not let anyone in, but it did not stop anyone either. The attacker still has your password, and they will keep trying, at a worse moment, or by calling you.",
        spot: ["A request while you were not signing in", "A location you are not in", "Several requests in a row"],
        protect: ["Choose No, it's not me", "Then choose Report, or call IT", "Several requests mean someone has your password"],
        remember: "Requests you did not start are an alarm. Raise it.",
        continueLabel: "Back to work",
      },
    },
    { id: "mfa_cut_office", type: "camera", waypointId: "wp_mfa_desk", environmentId: "office", fadeCut: true },

    // ---- Scene 3 · 09:15 · a genuine step-up check under time pressure
    { id: "mfa_title_0915", type: "title", title: "09:15", autoAdvanceMs: 2600, guide: { title: "Five minutes to go", body: "The report is finished." } },
    { id: "mfa_upload", type: "device", deviceId: "mfa_pc_upload", guide: { title: "Upload the report", body: "Click Upload report." }, completeOn: { action: "continue" } },
    { id: "mfa_cam_wide3", type: "camera", waypointId: "wp_mfa_desk_wide", mobileBuzz: true, guide: { title: "Your phone", body: "It buzzes." } },
    { id: "mfa_cam_phone3", type: "camera", waypointId: "wp_mfa_phone", mobileBuzz: true, guide: { title: "Your phone", body: "A sign-in request." } },
    { id: "mfa_auth_step", type: "device", deviceId: "mfa_phone_stepup", guide: { title: "Your phone", body: "Open the request." }, completeOn: { action: "continue" } },
    { id: "mfa_cam_back3", type: "camera", waypointId: "wp_mfa_desk_back" },
    { id: "mfa_uploaded", type: "device", deviceId: "mfa_pc_uploaded", guide: { title: "Report uploaded", body: "Click Go to the meeting." }, completeOn: { action: "continue" } },

    // ---- Scene 4 · 09:17 · the call from 'IT Security'
    { id: "mfa_cam_meeting", type: "camera", waypointId: "wp_mfa_meeting", walkSfx: true, mobileAt: "meeting", guide: { title: "Meeting room 2", body: "Alex is early for the meeting." } },
    { id: "mfa_cam_meeting_phone", type: "camera", waypointId: "wp_mfa_meeting_phone", mobileBuzz: true, guide: { title: "Your phone", body: "It rings on the table." } },
    { id: "mfa_call", type: "device", deviceId: "mfa_phone_call", guide: { title: "Your phone", body: "Answer the call." }, completeOn: { action: "continue" } },
    {
      id: "mfa_teach_call_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        whatIf: "If you had followed Daniel's instructions",
        body: "Caller ID can be faked, and a calm voice that knows your name and department proves nothing. You ended the call and phoned IT on the number you already knew. The real IT desk had never called you.",
        spot: ["An unexpected call about your account", "Urgency: before your account locks", "A request to type a number someone gives you"],
        protect: ["Hang up", "Call IT on the number you already know", "Report the call"],
        remember: "Real IT never needs you to type a number they read out to you.",
        continueLabel: "See what would have happened",
      },
    },
    { id: "mfa_trap_replay", type: "device", deviceId: "mfa_phone_replay", forced: true, whatIfNote: "If Alex had followed Daniel's instructions, a text message with a link would have arrived.", guide: { title: "What would have happened", body: "Wait for the request." }, completeOn: { action: "continue" } },

    // ---- Scene 5/6 · 09:20 · the attacker's side
    { id: "mfa_cut_hacker", type: "camera", waypointId: "wp_mfa_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, whatIfNote: REPLAY_NOTE, guide: { title: "Meanwhile", body: "Somewhere else." } },
    { id: "mfa_title_attacker", type: "title", title: "The attacker's side", kicker: "Attacker view", autoAdvanceMs: 3600, whatIfNote: REPLAY_NOTE, guide: { title: "The attacker's side", body: "How the attack was built." } },
    { id: "mfa_push", type: "camera", waypointId: "wp_mfa_hacker_laptop", whatIfNote: REPLAY_NOTE, guide: { title: "The attacker's laptop", body: "The attack log." } },
    { id: "mfa_dash_attack", type: "device", deviceId: "mfa_dash_attack", whatIfNote: REPLAY_NOTE, guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    { id: "mfa_dash_result", type: "device", deviceId: "mfa_dash_result", whatIfNote: REPLAY_NOTE, guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },

    // ---- outcomes
    {
      id: "mfa_teach_compromised",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "The number 68 came from the attacker's own sign-in screen, passed to you through a page and a phone call. By typing it, you approved the attacker's sign-in. Microsoft did exactly what it was told.",
        spot: ["A caller who gives you the number to type", "A 'verification' page that is not Microsoft or NorthBay", "A request you did not start, even with a local location"],
        protect: ["Tell IT straight away, even if you only suspect it", "IT signs you out of every session and resets your password", "IT checks what was read or sent from your account"],
        remember: "Never type a number someone else gives you. The number must come from your own screen.",
        continueLabel: "Report it to IT",
        altContinue: { label: "Try the key decision again", outcomeKey: "retry", prominent: true },
      },
    },
    { id: "mfa_retry_cut", type: "camera", waypointId: "wp_mfa_meeting_phone_cut", environmentId: "office", fadeCut: true, guide: { title: "One more time", body: "Back in the meeting room." } },
    { id: "mfa_retry", type: "device", deviceId: "mfa_phone_practice", guide: { title: "Try it again", body: "The same page, the same number." }, completeOn: { action: "continue" } },
    {
      id: "mfa_teach_retry_good",
      type: "teach",
      teach: {
        outcome: "safe",
        body: "That is the move. A request you did not start, a number handed to you by a caller: No, it's not me. Then report it.",
        spot: ["The number came from a caller, not your screen", "The page address was not Microsoft or NorthBay"],
        protect: ["No, it's not me", "Hang up and call IT on the known number"],
        remember: "The number must come from your own screen.",
        continueLabel: "Continue",
      },
    },
    {
      id: "mfa_teach_retry_again",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "Approved again. Remember who gave you the number: a caller, not your own screen. A request that comes with someone else's number is the attacker's.",
        spot: ["The number came from a caller, not your screen", "The page address was not Microsoft or NorthBay"],
        protect: ["No, it's not me", "Hang up and call IT on the known number"],
        remember: "The number must come from your own screen.",
        continueLabel: "Continue",
      },
    },
    {
      id: "mfa_teach_unresolved",
      type: "teach",
      teach: {
        outcome: "safe",
        heading: {
          eyebrow: "STOPPED, BUT NOT REPORTED",
          title: "The attacker is out. For now.",
          spotEyebrow: "WHAT YOU SAW",
          spotTitle: "The signs today",
          protectEyebrow: "STILL TO DO",
          protectTitle: "Close the door",
        },
        body: "You never approved the attacker's sign-in, so the account is safe for now. But nobody told IT Security, so the attacker still has the password and can try again tomorrow.",
        spot: ["Requests you did not start", "A call from 'IT' with a number to type", "A verification page outside Microsoft and NorthBay"],
        protect: ["Report it in Authenticator, or call IT on the known number", "IT resets the password and blocks the sign-ins", "IT warns colleagues about the fake caller"],
        remember: "Deny keeps them out today. Report keeps them out tomorrow.",
        continueLabel: "Continue",
      },
    },
    { id: "mfa_cam_end_compromised", type: "camera", waypointId: "wp_mfa_terminal", environmentId: "office", fadeCut: true, endsWhatIf: true },
    {
      id: "mfa_end_compromised",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "ACCOUNT COMPROMISED · CONTAINED",
        title: "Account compromised",
        cctv: false,
        lines: [
          "Alex reports the call to IT at 09:24.",
          "IT Security signs Alex out of every session, resets the password and checks which emails and files were opened.",
          "The attacker had four minutes in the mailbox. Reporting quickly kept it to four minutes.",
        ],
        continueLabel: "Continue",
      },
    },
    { id: "mfa_cam_end_protected", type: "camera", waypointId: "wp_mfa_terminal", environmentId: "office", fadeCut: true, endsWhatIf: true },
    {
      id: "mfa_end_protected",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "ACCOUNT PROTECTED",
        title: "Account protected",
        cctv: false,
        lines: [
          "The attacker never got into Alex's account.",
          "IT Security reset Alex's password and blocked the sign-ins from Amsterdam.",
          "Colleagues were warned about a fake 'Daniel from IT Security'.",
        ],
        continueLabel: "Continue",
      },
    },
    { id: "mfa_cam_end_unresolved", type: "camera", waypointId: "wp_mfa_terminal", environmentId: "office", fadeCut: true, endsWhatIf: true },
    {
      id: "mfa_end_unresolved",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "ATTACK STOPPED · INCIDENT UNRESOLVED",
        title: "Stopped, but not reported",
        cctv: false,
        lines: [
          "The attacker did not get into Alex's account today.",
          "Nobody told IT Security, so the password still works for the attacker.",
          "The next attempt comes on Thursday, during a deadline.",
        ],
        continueLabel: "Continue",
      },
    },
    {
      id: "mfa_debrief_terminal",
      type: "terminal_debrief",
      terminalDebrief: {
        intro: "Here is what happened, and the four things to remember.",
        continueLabel: "Continue to the quiz",
        topics: [
          {
            id: "mfa_t_yours",
            dimension: "identity",
            title: "Approve only what you started",
            lines: [
              "Your own sign-in: you started it, the app matches, and the number comes from your own screen.",
              "Two of today's requests were genuine. The right answer is not always No.",
            ],
          },
          {
            id: "mfa_t_fatigue",
            dimension: "identity",
            title: "Requests you did not start are an alarm",
            lines: [
              "They come from Microsoft's real sign-in service because someone else has your password.",
              "The location shown is approximate and can look local. It is not proof either way.",
            ],
          },
          {
            id: "mfa_t_caller",
            dimension: "identity",
            title: "Never type a number someone gives you",
            lines: [
              "Caller ID can be faked. Microsoft has documented attackers who pose as the helpdesk to get MFA approvals.",
              "Hang up and call IT on the number you already know.",
            ],
          },
          {
            id: "mfa_t_report",
            dimension: "operations",
            title: "Report, so IT can lock the door",
            lines: [
              "Denying keeps the attacker out today. Reporting lets IT reset your password and block them.",
              "Phishing-resistant sign-ins such as FIDO2 security keys and passkeys cannot be relayed like this.",
            ],
          },
        ],
      },
    },
    {
      id: "mfa_quiz",
      type: "quiz",
      title: "Check what you learned",
      quiz: [
        {
          id: "q_own",
          title: "Your own sign-in",
          prompt: "You open the Finance Portal and Authenticator asks for a number. When is it safe to approve?",
          options: [
            { id: "a", label: "Whenever the request says Microsoft" },
            { id: "b", label: "When you started the sign-in and the number comes from your own screen", correct: true },
            { id: "c", label: "When the location shown is your city" },
          ],
          rationale: "A genuine request is one you started, for the app you are opening, with the number on your own screen.",
        },
        {
          id: "q_unexpected",
          title: "A request you did not start",
          prompt: "You are working in a spreadsheet and your phone asks you to approve a sign-in. What do you do?",
          options: [
            { id: "a", label: "Choose No, it's not me, and report it", correct: true },
            { id: "b", label: "Ignore it, it will time out" },
            { id: "c", label: "Type any number to make it stop" },
          ],
          rationale: "A request you did not start means someone has your password. Deny it and report it so IT can act.",
        },
        {
          id: "q_burst",
          title: "Several requests in a row",
          prompt: "Three sign-in requests arrive within two minutes. What do they most likely mean?",
          options: [
            { id: "a", label: "Authenticator has a fault" },
            { id: "b", label: "Someone has your password and hopes you approve one", correct: true },
            { id: "c", label: "IT is testing the system" },
          ],
          rationale: "This is MFA fatigue: the attacker keeps trying until someone approves out of habit or annoyance.",
        },
        {
          id: "q_stepup",
          title: "A step-up check",
          prompt: "You click Upload report and Microsoft asks for another check with a number on your screen. What do you do?",
          options: [
            { id: "a", label: "Deny it, because one request today was suspicious" },
            { id: "b", label: "Approve it with the number from your screen", correct: true },
            { id: "c", label: "Wait until the meeting is over" },
          ],
          rationale: "You started it and the number is on your own screen. Denying every request stops your own work.",
        },
        {
          id: "q_caller",
          title: "The call from 'IT'",
          prompt: "A caller from 'IT Security' knows your name and department and asks you to type a number into Authenticator. What do you do?",
          options: [
            { id: "a", label: "Type it, IT knows your details" },
            { id: "b", label: "Ask him to send the number by email instead" },
            { id: "c", label: "Hang up and call IT on the number you already know", correct: true },
          ],
          rationale: "Caller ID and personal details are not proof. Real IT never needs you to type a number they give you.",
        },
        {
          id: "q_strong",
          title: "Stronger sign-ins",
          prompt: "Which kind of sign-in cannot be relayed through a fake page like this?",
          options: [
            { id: "a", label: "A FIDO2 security key or passkey", correct: true },
            { id: "b", label: "A text message code" },
            { id: "c", label: "A longer password" },
          ],
          rationale: "Phishing-resistant methods are bound to the real website, so a fake page cannot pass them on.",
        },
      ],
    },
    { id: "mfa_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "mfa_intro", to: "mfa_cam_terminal" },
    { from: "mfa_cam_terminal", to: "mfa_terminal" },
    { from: "mfa_terminal", to: "mfa_cam_desk" },
    { from: "mfa_cam_desk", to: "mfa_login" },
    { from: "mfa_login", to: "mfa_intranet" },
    { from: "mfa_intranet", to: "mfa_signin" },
    { from: "mfa_signin", to: "mfa_cam_wide1" },
    { from: "mfa_cam_wide1", to: "mfa_cam_phone1" },
    { from: "mfa_cam_phone1", to: "mfa_auth_own" },
    { from: "mfa_auth_own", to: "mfa_cam_back1" },
    { from: "mfa_cam_back1", to: "mfa_portal" },
    { from: "mfa_portal", to: "mfa_title_0912" },
    { from: "mfa_title_0912", to: "mfa_sheet" },
    { from: "mfa_sheet", to: "mfa_cam_wide2" },
    { from: "mfa_cam_wide2", to: "mfa_cam_phone2" },
    { from: "mfa_cam_phone2", to: "mfa_bomb" },
    { from: "mfa_bomb", to: "mfa_peek_cut" },
    { from: "mfa_peek_cut", to: "mfa_peek_title" },
    { from: "mfa_peek_title", to: "mfa_peek_push" },
    { from: "mfa_peek_push", to: "mfa_peek_dash" },
    { from: "mfa_peek_dash", to: "mfa_teach_bomb_tried", on: "attempted" },
    { from: "mfa_peek_dash", to: "mfa_teach_bomb_reported", on: "reported" },
    { from: "mfa_peek_dash", to: "mfa_teach_bomb_denied", on: "denied" },
    { from: "mfa_peek_dash", to: "mfa_teach_bomb_ignored", on: "ignored" },
    { from: "mfa_peek_dash", to: "mfa_teach_bomb_ignored" },
    { from: "mfa_teach_bomb_tried", to: "mfa_cut_office" },
    { from: "mfa_teach_bomb_reported", to: "mfa_cut_office" },
    { from: "mfa_teach_bomb_denied", to: "mfa_cut_office" },
    { from: "mfa_teach_bomb_ignored", to: "mfa_cut_office" },
    { from: "mfa_cut_office", to: "mfa_title_0915" },
    { from: "mfa_title_0915", to: "mfa_upload" },
    { from: "mfa_upload", to: "mfa_cam_wide3" },
    { from: "mfa_cam_wide3", to: "mfa_cam_phone3" },
    { from: "mfa_cam_phone3", to: "mfa_auth_step" },
    { from: "mfa_auth_step", to: "mfa_cam_back3" },
    { from: "mfa_cam_back3", to: "mfa_uploaded" },
    { from: "mfa_uploaded", to: "mfa_cam_meeting" },
    { from: "mfa_cam_meeting", to: "mfa_cam_meeting_phone" },
    { from: "mfa_cam_meeting_phone", to: "mfa_call" },
    { from: "mfa_call", to: "mfa_teach_call_safe", on: "safe" },
    { from: "mfa_call", to: "mfa_cut_hacker", on: "approved" },
    { from: "mfa_call", to: "mfa_cut_hacker", on: "rejected" },
    { from: "mfa_call", to: "mfa_cut_hacker" },
    { from: "mfa_teach_call_safe", to: "mfa_trap_replay" },
    { from: "mfa_trap_replay", to: "mfa_cut_hacker" },
    { from: "mfa_cut_hacker", to: "mfa_title_attacker" },
    { from: "mfa_title_attacker", to: "mfa_push" },
    { from: "mfa_push", to: "mfa_dash_attack" },
    { from: "mfa_dash_attack", to: "mfa_dash_result" },
    { from: "mfa_dash_result", to: "mfa_teach_compromised", on: "compromised" },
    { from: "mfa_dash_result", to: "mfa_cam_end_protected", on: "protected" },
    { from: "mfa_dash_result", to: "mfa_teach_unresolved", on: "unresolved" },
    { from: "mfa_dash_result", to: "mfa_teach_unresolved" },
    { from: "mfa_teach_compromised", to: "mfa_retry_cut", on: "retry" },
    { from: "mfa_teach_compromised", to: "mfa_cam_end_compromised" },
    { from: "mfa_retry_cut", to: "mfa_retry" },
    { from: "mfa_retry", to: "mfa_teach_retry_again", on: "approved" },
    { from: "mfa_retry", to: "mfa_teach_retry_good", on: "rejected" },
    { from: "mfa_retry", to: "mfa_teach_retry_good" },
    { from: "mfa_teach_retry_good", to: "mfa_cam_end_compromised" },
    { from: "mfa_teach_retry_again", to: "mfa_cam_end_compromised" },
    { from: "mfa_teach_unresolved", to: "mfa_cam_end_unresolved" },
    { from: "mfa_cam_end_compromised", to: "mfa_end_compromised" },
    { from: "mfa_cam_end_protected", to: "mfa_end_protected" },
    { from: "mfa_cam_end_unresolved", to: "mfa_end_unresolved" },
    { from: "mfa_end_compromised", to: "mfa_debrief_terminal" },
    { from: "mfa_end_protected", to: "mfa_debrief_terminal" },
    { from: "mfa_end_unresolved", to: "mfa_debrief_terminal" },
    { from: "mfa_debrief_terminal", to: "mfa_quiz" },
    { from: "mfa_quiz", to: "mfa_debrief" },
  ],
};
